import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpEvent, HttpEventType } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/interval';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/startWith';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/of';
import { environment } from '../../environments/environment';

export interface WhatsAppMessage {
  id: number;
  customer_wa_id: string;
  sender_name?: string;
  message_text: string;
  media_url?: string;
  media_type?: string;
  received_at: string;
  is_read: string;
  salon_id: number;
}

export interface ChatConversation {
  customer_wa_id: string;
  customer_phone?: string;
  customer_username?: string;
  sender_name?: string;
  is_salon_customer?: boolean;
  last_message: string;
  last_message_time: string;
  unread_count: number;
  can_reply: boolean;
}

@Injectable()
export class WhatsAppChatService {
  private apiUrl: string = environment.apiUrl;
  private messagesSubject = new BehaviorSubject<WhatsAppMessage[]>([]);
  public messages$ = this.messagesSubject.asObservable();
  private currentUser: any;
  private pollingStarted = false;

  constructor(private http: HttpClient) {
    // Check if user is already logged in and start polling
    this.currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    if (this.shouldPoll()) {
      this.startPolling();
      this.pollingStarted = true;
    }
  }

  private shouldPoll(): boolean {
    return this.currentUser.salon_id &&
      this.currentUser.whatsAppEnabled === '1' &&
      this.currentUser.incomingMessage === '1';
  }

  public startPollingIfAllowed() {
    // Refresh currentUser from localStorage
    this.currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');

    if (this.shouldPoll() && !this.pollingStarted) {
      this.startPolling();
      this.pollingStarted = true;
    }
  }

  private startPolling() {
    Observable.interval(5000)
      .startWith(0)
      .switchMap(() => {
        return this.getIncomingMessages()
          .catch(error => {
            // console.error('Error fetching messages:', error);
            return Observable.of([]);
          });
      })
      .subscribe(messages => {
        this.messagesSubject.next(messages);
      });
  }

  getIncomingMessages(): Observable<WhatsAppMessage[]> {
    return this.http.post<any>(`${this.apiUrl}getIncomingMessages`, JSON.stringify({
      salon_id: this.currentUser.salon_id,
      currentUser: this.currentUser
    }))
      .map(response => {
        if (response && response.data) {
          return response.data;
        }
        return response || [];
      });
  }

  getConversations(): Observable<ChatConversation[]> {
    return this.http.post<any>(`${this.apiUrl}getConversations`, JSON.stringify({
      salon_id: this.currentUser.salon_id,
      currentUser: this.currentUser
    }))
      .map(response => {
        if (response && response.data) {
          return response.data;
        }
        return response || [];
      });
  }

  getConversationMessages(customerWaId: string): Observable<WhatsAppMessage[]> {
    return this.http.post<any>(`${this.apiUrl}getConversationMessages`, JSON.stringify({
      salon_id: this.currentUser.salon_id,
      customer_wa_id: customerWaId,
      currentUser: this.currentUser
    }))
      .map(response => {
        if (response && response.data) {
          return response.data;
        }
        return response || [];
      });
  }

  sendMessage(phoneNumber: string, message: string): Observable<any> {
    return this.http.post(`${this.apiUrl}sendMessage`, JSON.stringify({
      salon_id: this.currentUser.salon_id,
      phone_number: phoneNumber,
      customer_wa_id: phoneNumber,
      message: message,
      currentUser: this.currentUser
    }));
  }

  markAsRead(messageId: number): Observable<any> {
    return this.http.post(`${this.apiUrl}markAsRead`, JSON.stringify({
      salon_id: this.currentUser.salon_id,
      message_id: messageId,
      currentUser: this.currentUser
    }));
  }

  markConversationAsRead(customerWaId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}markConversationAsRead`, JSON.stringify({
      salon_id: this.currentUser.salon_id,
      customer_wa_id: customerWaId,
      currentUser: this.currentUser
    }));
  }

  canReplyToCustomer(customerWaId: string): Observable<{ can_reply: boolean }> {
    return this.http.post<{ can_reply: boolean }>(`${this.apiUrl}canReply`, JSON.stringify({
      salon_id: this.currentUser.salon_id,
      customer_wa_id: customerWaId,
      currentUser: this.currentUser
    }));
  }
}



// WEBPACK FOOTER //
// ./src/app/services/whatsapp-chat.service.ts